import {
  phSubstances,
  PH_BAND_ORDER,
  type PhBand,
  type PhSubstance,
} from "./substances";

/**
 * pH Sort - pure question-generation + scoring logic.
 *
 * Each question shows one substance and asks the player to drop it into
 * one of five pH bands. Exact match = full points; adjacent band = partial
 * credit (so a "weak-acid" answered as "strong-acid" still earns a little);
 * other / wrong / timeout = 0.
 *
 * Scoring matches the bundle's speed-bonus convention so muscle memory
 * carries between games (Glassware ID, Speed Quiz, Reaction Race).
 */

export const PH_TURN_SECONDS = 10;
export const PH_FAST_THRESHOLD_S = 3;
export const PH_POINTS_FAST = 15;
export const PH_POINTS_SLOW = 8;
export const PH_POINTS_ADJACENT = 3;

export type PhOutcome = "fast" | "slow" | "adjacent" | "wrong" | "timeout";

export interface PhQuestion {
  id: string;
  substance: PhSubstance;
  /** All five band keys in display order. */
  bands: readonly PhBand[];
}

function pickRandom<T>(rng: () => number, arr: T[]): T {
  return arr[Math.floor(rng() * arr.length)];
}

/**
 * Build a single question. Tries to honour `excludeIds`; falls back to
 * the unfiltered pool if every candidate would be excluded so long
 * sessions never deadlock.
 */
export function generateQuestion(
  rng: () => number,
  excludeIds: ReadonlySet<string>,
): PhQuestion {
  const available = phSubstances.filter((s) => !excludeIds.has(s.id));
  const pool = available.length > 0 ? available : phSubstances;
  const target = pickRandom(rng, pool);
  return {
    id: target.id,
    substance: target,
    bands: PH_BAND_ORDER,
  };
}

/**
 * Distance in band steps between two bands (0 = same, 1 = adjacent, etc.).
 */
export function bandDistance(a: PhBand, b: PhBand): number {
  return Math.abs(PH_BAND_ORDER.indexOf(a) - PH_BAND_ORDER.indexOf(b));
}

/** Resolve an answer attempt into an awarded point value + outcome. */
export function scoreAnswer(
  chosen: PhBand | null,
  question: PhQuestion,
  elapsedMs: number,
): { points: number; outcome: PhOutcome } {
  if (chosen === null) return { points: 0, outcome: "timeout" };
  const distance = bandDistance(chosen, question.substance.band);
  if (distance === 0) {
    if (elapsedMs <= PH_FAST_THRESHOLD_S * 1000) {
      return { points: PH_POINTS_FAST, outcome: "fast" };
    }
    return { points: PH_POINTS_SLOW, outcome: "slow" };
  }
  if (distance === 1) {
    return { points: PH_POINTS_ADJACENT, outcome: "adjacent" };
  }
  return { points: 0, outcome: "wrong" };
}

export type { PhBand, PhSubstance };